import Button from '../shared/Button.js';
import React from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {recordSeqNavOptions} from '../lib/recordSequence.js';
import {buildRuminantWeighInSessionColumns} from '../lib/weighInSessionExports.js';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {printRows} from '../lib/printExport.js';
import {createSavedView, updateSavedView, deleteSavedView} from '../lib/savedViewsApi.js';
import {savedViewsErrorMessage, useSavedViewsData} from '../shared/savedViewsData.js';
import SheepNewWeighInModal from './SheepNewWeighInModal.tsx';
import {createWeighInSession} from '../lib/weighInApi.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {SheepWeighInsListData} from '../app/data/routeDataCatalog.js';
import {useRouteResource, type UseRouteResourceReturn} from '../app/data/useRouteResource.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import DataTable from '../shared/DataTable.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Badge from '../shared/Badge.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import StatusText from '../shared/StatusText.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice from '../shared/InlineNotice.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import PlannerIcon from '../components/PlannerIcon.jsx';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
import {getProgramColor} from '../lib/programColors.js';
import {sb} from '../lib/supabase.js';
import {fmt} from '../lib/dateUtils.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useUI} from '../contexts/UIContext.jsx';
import Header from '../shared/Header.tsx';
import {
  RuminantGroupLabel,
  RuminantWeighInsHeaderSummary,
  ruminantSavedViewInputStyle,
  ruminantSavedViewRadioLabelStyle,
  ruminantWeighInEmptyBoxStyle,
  ruminantWeighInEntryMatchText,
} from '../cattle/ruminantPresentation.js';
import {SHEEP_FLOCK_KEYS} from '../lib/sheepFlockFilters.js';
import type {AuthIdentity} from '../contexts/AuthContext.jsx';
import type {Notice} from '../shared/InlineNotice.jsx';
import type {NewSheepWeighInSession} from './SheepNewWeighInModal.tsx';
import type {WeighInEntryRow} from '../lib/weighInSessionExports.js';

// A weigh_in_sessions row. The read is `select('*')`, so the row is an open
// record with only the columns this list actually names declared.
type SheepWeighInSessionRow = {
  [column: string]: unknown;
  id: string;
  date: string | null;
  herd: string | null;
  status: string | null;
  team_member: string | null;
};

// A weigh_ins row as THIS list reads it. The scoped resource answers rows from
// `weigh_ins select('*')`, so the three columns grouped / filtered / counted
// here are named at the consumer boundary.
type SheepWeighInEntryRow = {
  [column: string]: unknown;
  session_id: string;
  tag: string | null;
  new_tag_flag: boolean | null;
};

// A saved_views row, reduced to the columns this surface reads. `view_state` is
// jsonb, so its own fields stay `unknown` and are checked before use.
type SavedViewRow = {
  id: string;
  name: string;
  visibility?: string | null;
  owner_profile_id?: string | null;
  view_state?: unknown;
};
// The shape sheepWeighInsViewState() writes. Declared as the written record
// rather than per-field `unknown` so the restore below stays the exact
// expression its guard pins; the runtime checks it already made still run.
type SavedWeighInsViewState = {statusFilter: string; tagSearch: string};
type ErrorLike = {message?: string | null};

// The subset of the auth identity SheepNewWeighInModal reads to lock the team
// member field. Declared here rather than imported because the modal does not
// export it - the same local declaration CattleWeighInsView makes.
type LockedTeamMemberSource = {
  name?: string | null;
  profile?: {name?: string | null; full_name?: string | null} | null;
  user?: {email?: string | null} | null;
};

// The two `herd &&` guards in front of a FLOCK_LABELS lookup below are what a
// nullable key costs; they change no result, because a null herd missed the map
// before and short-circuits to the same fallback now. Same shape CattleHerdsView
// uses for its snapshot-group lookup.
const FLOCK_LABELS: Record<string, string> = {rams: 'Rams', ewes: 'Ewes', feeders: 'Feeders'};
const SHEEP_WEIGHINS_SURFACE_KEY = 'sheep.weighins';
const SHEEP_WEIGHINS_PARAMS = Object.freeze({});
const VALID_WEIGHIN_STATUS_FILTERS = new Set(['all', 'draft', 'complete']);
const EXTENDED_LIST_CONTROLS_ENABLED = false;

const SheepWeighInsView = () => {
  const {authState} = useAuth() as Omit<ReturnType<typeof useAuth>, 'authState'> & {
    authState: AuthIdentity | null;
  };
  const {setView} = useUI();
  const navigate = useNavigate();
  // `location.state` is `unknown` by design (src/app/navigation/appLocation.ts).
  // The one shape this view reads is the destination batch written by "Send
  // sheep to processor" on Sheep Batches and on a batch record, so the proof is
  // applied once, here at the hook call.
  const location = useLocation() as Omit<ReturnType<typeof useLocation>, 'state'> & {
    state?: {sendToBatch?: {id?: string | null; name?: string | null} | null} | null;
  };
  // Carried into every session this view opens, so the Send-to-Processor modal
  // at the end of that session preselects the batch the operator already chose.
  const sendToBatch = (location.state && location.state.sendToBatch) || null;
  const sendToBatchState = sendToBatch && sendToBatch.id ? {sendToBatch} : null;
  const {useState, useEffect} = React;
  const sheepWeighIns = useRouteResource(RESOURCE.SHEEP_WEIGHINS_LIST, SHEEP_WEIGHINS_PARAMS) as Omit<
    UseRouteResourceReturn,
    'data' | 'error'
  > & {
    data: SheepWeighInsListData | null;
    error: ErrorLike | null;
  };
  const sessions: SheepWeighInSessionRow[] = sheepWeighIns.data
    ? (sheepWeighIns.data.sessions as SheepWeighInSessionRow[])
    : [];
  const entries: Record<string, SheepWeighInEntryRow[]> = sheepWeighIns.data
    ? (sheepWeighIns.data.entries as Record<string, SheepWeighInEntryRow[]>)
    : {};
  const loading = sheepWeighIns.loading;
  const loadError: Notice | null = sheepWeighIns.error
    ? {
        kind: 'error',
        message:
          'Could not load sheep weigh-in sessions. Please refresh the page. (' +
          (sheepWeighIns.error.message || String(sheepWeighIns.error)) +
          ')',
      }
    : null;
  const loadAll = sheepWeighIns.refetch;
  const [statusFilter, setStatusFilter] = usePersistentViewState('sheep.weighins.statusFilter', 'all');
  const [showNewModal, setShowNewModal] = useState(false);
  const [tagSearch, setTagSearch] = usePersistentViewState('sheep.weighins.tagSearch', '');
  const [notice, setNotice] = useState<Notice | null>(null);
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showSaveViewForm, setShowSaveViewForm] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewVisibility, setSaveViewVisibility] = useState('private');
  const [savedViewBusy, setSavedViewBusy] = useState(false);
  const [savedViewNotice, setSavedViewNotice] = useState<Notice | null>(null);
  const myProfileId = authState?.user?.id || null;
  const savedViewsQuery = useSavedViewsData(SHEEP_WEIGHINS_SURFACE_KEY, {client: sb});
  const savedViews = savedViewsQuery.data as SavedViewRow[];
  const savedViewsError = savedViewsQuery.error ? savedViewsErrorMessage(savedViewsQuery.error) : null;
  const savedViewsLoading = savedViewsQuery.loading;
  const loadSavedViews = savedViewsQuery.refetch;
  // AuthIdentity declares `profile` as `unknown`, so the modal's narrower
  // team-member view is stated here. `false` and `null` both become null, which
  // is what the modal already resolved them to.
  const lockedAuthState: LockedTeamMemberSource | null =
    authState && typeof authState === 'object' ? (authState as LockedTeamMemberSource) : null;

  useEffect(() => {
    if (savedViewsLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViews.some((row) => row.id === cur) ? cur : ''));
  }, [savedViews, savedViewsError, savedViewsLoading]);

  async function createNewSession(opts: NewSheepWeighInSession) {
    const id = 'wsess-' + String(Date.now()) + Math.random().toString(36).slice(2, 6);
    // Tier-2 write boundary (migration 196): weigh_in_sessions grants no
    // INSERT to authenticated. status/started_at are server-owned/defaulted
    // by the RPC (status is always created 'draft').
    const rec = {
      id,
      date: opts.date,
      team_member: opts.team_member,
      species: 'sheep',
      herd: opts.herd,
    };
    await createWeighInSession(sb, rec);
    setShowNewModal(false);
    navigate('/weigh-in-sessions/' + id, sendToBatchState ? {state: sendToBatchState} : undefined);
  }

  const effectiveStatusFilter = EXTENDED_LIST_CONTROLS_ENABLED ? statusFilter : 'all';
  const effectiveTagSearch = EXTENDED_LIST_CONTROLS_ENABLED ? tagSearch : '';
  const tagQ = effectiveTagSearch.trim().toLowerCase();
  const entryMatchesTag = (e: WeighInEntryRow & {tag?: string | null}) =>
    !tagQ || (e.tag || '').toLowerCase().includes(tagQ);
  const statusFiltered = sessions.filter((s) => effectiveStatusFilter === 'all' || s.status === effectiveStatusFilter);
  const filtered = tagQ ? statusFiltered.filter((s) => (entries[s.id] || []).some(entryMatchesTag)) : statusFiltered;
  const totalEntries = filtered.reduce((s, sess) => s + (entries[sess.id] || []).length, 0);
  const matchedSessionCount = tagQ ? filtered.length : null;
  const loadFailed = !!loadError;
  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);
  const emptyStateKind = sessions.length === 0 ? 'none' : 'filtered';
  const emptyStateMessage =
    emptyStateKind === 'none'
      ? 'No sheep weigh-in sessions yet.'
      : tagQ
        ? 'No sheep weigh-in sessions match #' + tagSearch + '.'
        : 'No sheep weigh-in sessions match the current filters.';
  const emptyStateHint =
    emptyStateKind === 'none'
      ? 'Click New Weigh-In to start one.'
      : tagQ
        ? 'Clear the tag search or switch back to All.'
        : 'Switch back to All to see every session.';

  function sheepWeighInsViewState() {
    return {
      statusFilter: VALID_WEIGHIN_STATUS_FILTERS.has(statusFilter) ? statusFilter : 'all',
      tagSearch: tagSearch || '',
    };
  }

  function applySheepSavedView(view: SavedViewRow | null | undefined) {
    if (!view) return;
    const st = (view.view_state || {}) as SavedWeighInsViewState;
    setStatusFilter(VALID_WEIGHIN_STATUS_FILTERS.has(st.statusFilter) ? st.statusFilter : 'all');
    setTagSearch(typeof st.tagSearch === 'string' ? st.tagSearch : '');
    setSavedViewNotice(null);
  }

  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    setSavedViewNotice(null);
    if (!id) return;
    applySheepSavedView(savedViews.find((v) => v.id === id));
  }

  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setSavedViewNotice(null);
    setShowSaveViewForm(true);
  }

  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setSavedViewNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      const created = (await createSavedView(sb, {
        surfaceKey: SHEEP_WEIGHINS_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: sheepWeighInsViewState(),
      })) as {id?: string} | null;
      setShowSaveViewForm(false);
      setSavedViewNotice({kind: 'success', message: 'Saved view "' + name + '".'});
      await loadSavedViews();
      if (created?.id) setSelectedViewId(created.id);
    } catch (caught) {
      const e = caught as ErrorLike;
      setSavedViewNotice({kind: 'error', message: 'Save view failed: ' + (e.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      await updateSavedView(sb, selectedView.id, {viewState: sheepWeighInsViewState()});
      await loadSavedViews();
      setSavedViewNotice({
        kind: 'success',
        message: 'Updated "' + selectedView.name + '" to the current filter/search.',
      });
    } catch (caught) {
      const e = caught as ErrorLike;
      setSavedViewNotice({kind: 'error', message: 'Update view failed: ' + (e.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function proceedDeleteSelectedView(view: SavedViewRow) {
    setSavedViewBusy(true);
    try {
      await deleteSavedView(sb, view.id);
      setSelectedViewId('');
      await loadSavedViews();
      setSavedViewNotice({kind: 'success', message: 'Deleted saved view "' + view.name + '".'});
    } catch (caught) {
      const e = caught as ErrorLike;
      setSavedViewNotice({kind: 'error', message: 'Delete view failed: ' + (e.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete saved view "' + selectedView.name + '"?', () => {
      void proceedDeleteSelectedView(selectedView);
    });
  }

  function sheepWeighInsExportColumns() {
    return buildRuminantWeighInSessionColumns({
      groupHeader: 'Flock',
      groupLabels: FLOCK_LABELS,
      entriesBySession: entries,
      tagQ,
      entryMatchesTag,
    });
  }

  function handleExportCsv() {
    const columns = sheepWeighInsExportColumns();
    const ok = downloadCsv(csvFilename('sheep-weigh-in-sessions'), rowsToCsv(columns, filtered));
    if (!ok) setNotice({kind: 'error', message: 'CSV export is only available in the browser.'});
  }

  function handlePrintRows() {
    const columns = sheepWeighInsExportColumns();
    const ok = printRows({
      title: 'Sheep Weigh-In Sessions',
      subtitle: filtered.length + ' filtered weigh-in sessions',
      columns,
      rows: filtered,
    });
    if (!ok) setNotice({kind: 'error', message: 'Print is only available in the browser.'});
  }

  const myViews = savedViews.filter((v) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );
  const savedViewInputS = ruminantSavedViewInputStyle;
  const savedViewRadioLabelS = ruminantSavedViewRadioLabelStyle;

  return (
    <div
      style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      data-weighin-list-loaded={loading || loadFailed ? 'false' : 'true'}
    >
      <Header />
      <div style={{padding: '1rem', maxWidth: 1180, margin: '0 auto'}}>
        <InlineNotice notice={loadError || notice} />

        {/* The batch this visit is sending to. Shown because the choice was made
            on another screen: without it the preselected destination would only
            appear at the confirmation, which is too late to notice it is wrong.
            Clearing it returns to the ordinary flow rather than blocking.
            A context strip, so the info tone (N13) - not a program-colour
            border, which is decoration (R05). */}
        {sendToBatch && sendToBatch.id && (
          <div
            data-sheep-weighins-send-target={sendToBatch.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              marginBottom: 10,
              padding: '9px 12px',
              borderRadius: 'var(--r-control)',
              background: 'var(--info-soft)',
              fontSize: 12,
              color: 'var(--ink)',
            }}
          >
            <span style={{flex: 1, minWidth: 240}}>
              Sending to <strong>{sendToBatch.name || sendToBatch.id}</strong>. Open or start a weigh-in session, flag
              the sheep, then complete the session to confirm.
            </span>
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate('/sheep/weighins', {replace: true, state: null})}
            >
              Clear
            </Button>
          </div>
        )}
        {loadFailed && (
          <Button variant="secondary" type="button" onClick={loadAll} style={{marginBottom: 12}}>
            Retry
          </Button>
        )}
        {EXTENDED_LIST_CONTROLS_ENABLED && !loadFailed && (
          <>
            <div
              data-sheep-weighins-saved-views-row
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
              {savedViewsError ? (
                <span style={{fontSize: 12, color: 'var(--danger-ink)'}} data-sheep-weighins-saved-views-error>
                  Saved views unavailable. Filters still work.
                </span>
              ) : (
                <>
                  <select
                    data-sheep-weighins-saved-view-select
                    value={selectedViewId}
                    disabled={savedViewsLoading}
                    onChange={(e) => onSelectSavedView(e.target.value)}
                    style={{...savedViewInputS, width: 'auto', minWidth: 200}}
                  >
                    <option value="">{savedViewsLoading ? 'Loading...' : 'Select a saved view'}</option>
                    {myViews.length > 0 && (
                      <optgroup label="My views">
                        {myViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name + (v.visibility === 'public' ? ' - public' : ' - private')}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {publicOtherViews.length > 0 && (
                      <optgroup label="Public views">
                        {publicOtherViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {selectedViewIsMine && (
                    <>
                      <Button
                        variant="secondary"
                        type="button"
                        data-sheep-weighins-saved-view-update
                        onClick={updateSelectedView}
                        disabled={savedViewBusy}
                      >
                        Update to current
                      </Button>
                      <Button
                        variant="danger"
                        type="button"
                        data-sheep-weighins-saved-view-delete
                        onClick={deleteSelectedView}
                        disabled={savedViewBusy}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  <span style={{flex: 1}} />
                  {savedViewNotice && (
                    <span
                      style={{
                        fontSize: 12,
                        color: savedViewNotice.kind === 'success' ? 'var(--ok-ink)' : 'var(--danger)',
                      }}
                    >
                      {savedViewNotice.message}
                    </span>
                  )}
                  <Button
                    variant="secondary"
                    type="button"
                    data-sheep-weighins-saved-view-save-open
                    onClick={openSaveViewForm}
                    disabled={savedViewBusy || savedViewsLoading}
                  >
                    Save current view
                  </Button>
                </>
              )}
            </div>
            {showSaveViewForm && (
              <div
                data-sheep-weighins-saved-view-form
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <input
                  data-sheep-weighins-saved-view-name
                  type="text"
                  value={saveViewName}
                  placeholder="View name"
                  onChange={(e) => setSaveViewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSaveView();
                  }}
                  style={{...savedViewInputS, flex: 1, minWidth: 200}}
                />
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveSheepWeighInsViewVisibility"
                    checked={saveViewVisibility === 'private'}
                    onChange={() => setSaveViewVisibility('private')}
                    data-sheep-weighins-saved-view-visibility="private"
                  />
                  Private
                </label>
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveSheepWeighInsViewVisibility"
                    checked={saveViewVisibility === 'public'}
                    onChange={() => setSaveViewVisibility('public')}
                    data-sheep-weighins-saved-view-visibility="public"
                  />
                  Public
                </label>
                <Button
                  variant="secondary"
                  type="button"
                  data-sheep-weighins-saved-view-save
                  onClick={submitSaveView}
                  disabled={savedViewBusy}
                >
                  Save
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setShowSaveViewForm(false)}
                  disabled={savedViewBusy}
                >
                  Cancel
                </Button>
              </div>
            )}
          </>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <h1 className="wcf-page-title">Sheep Weigh-In Sessions</h1>
            <div style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 2}}>
              <RuminantWeighInsHeaderSummary
                tagQ={tagQ}
                tagSearch={tagSearch}
                matchedSessionCount={matchedSessionCount}
                sessionCount={filtered.length}
                totalEntries={totalEntries}
              />
            </div>
          </div>
          <div style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'}}>
            {EXTENDED_LIST_CONTROLS_ENABLED && (
              <>
                <div style={{position: 'relative'}}>
                  <input
                    type="search"
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    placeholder="Search by tag #..."
                    style={{
                      fontFamily: 'inherit',
                      fontSize: 12,
                      padding: '6px 28px 6px 10px',
                      border: '1px solid var(--border-strong)',
                      borderRadius: 10,
                      width: 160,
                      boxSizing: 'border-box',
                      background: 'var(--surface)',
                      color: 'var(--ink)',
                      outline: 'none',
                    }}
                  />
                  {tagSearch && (
                    <button
                      type="button"
                      onClick={() => setTagSearch('')}
                      title="Clear search"
                      style={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        fontSize: 16,
                        lineHeight: 1,
                        color: 'var(--ink-faint)',
                        cursor: 'pointer',
                        padding: '2px 6px',
                        fontFamily: 'inherit',
                      }}
                    >
                      {'×'}
                    </button>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    borderRadius: 10,
                    overflow: 'hidden',
                    border: '1px solid var(--border-strong)',
                  }}
                >
                  {[
                    {k: 'all', l: 'All'},
                    {k: 'draft', l: 'Drafts'},
                    {k: 'complete', l: 'Complete'},
                  ].map((o, oi) => (
                    <button
                      key={o.k}
                      onClick={() => setStatusFilter(o.k)}
                      style={{
                        padding: '5px 10px',
                        border: 'none',
                        borderRight: oi < 2 ? '1px solid var(--border-strong)' : 'none',
                        fontFamily: 'inherit',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: 'var(--surface)',
                        color: statusFilter === o.k ? getProgramColor('sheep') : 'var(--ink-muted)',
                      }}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
                <Button
                  variant="secondary"
                  type="button"
                  data-sheep-weighins-export-csv="1"
                  onClick={handleExportCsv}
                  disabled={loading || loadFailed}
                >
                  Export CSV
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  data-sheep-weighins-print="1"
                  onClick={handlePrintRows}
                  disabled={loading || loadFailed}
                >
                  Print
                </Button>
              </>
            )}
            <Button
              variant="primary"
              data-new-weighin-button="1"
              onClick={() => setShowNewModal(true)}
              style={{display: 'inline-flex', alignItems: 'center', gap: 6}}
            >
              <PlannerIcon iconKey="weighins" size={16} />
              <span>New Weigh-In</span>
            </Button>
          </div>
        </div>

        {loading && <div style={{textAlign: 'center', padding: '2rem', color: 'var(--ink-faint)'}}>Loading{'…'}</div>}
        {!loading && !loadFailed && filtered.length === 0 && (
          <div
            data-weighin-empty-state="1"
            data-weighin-empty-kind={emptyStateKind}
            style={ruminantWeighInEmptyBoxStyle}
          >
            <div style={{fontWeight: 700, color: 'var(--ink)', marginBottom: 4}}>{emptyStateMessage}</div>
            <div>{emptyStateHint}</div>
          </div>
        )}

        {!loadFailed && filtered.length > 0 && (
          <DataTable
            surfaceKey="sheep-weighins-table"
            rows={filtered}
            rowKey="id"
            density="comfortable"
            rowProps={(s) => ({'data-weighin-session-tile': s.id})}
            card={{
              main: (s) => ((s.herd && FLOCK_LABELS[s.herd]) || s.herd || 'Unknown Flock') + ' · ' + fmt(s.date),
              line1: (s) => {
                const all = entries[s.id] || [];
                const shown = ruminantWeighInEntryMatchText(all, tagQ, entryMatchesTag);
                return [s.team_member, shown].filter(Boolean).join(' · ');
              },
              line2: () => '',
              badge: (s) => {
                const newTags = tagQ ? 0 : (entries[s.id] || []).filter((entry) => entry.new_tag_flag).length;
                return newTags > 0 ? (
                  <Badge variant="warn">
                    {newTags} New {newTags === 1 ? 'Tag' : 'Tags'}
                  </Badge>
                ) : (
                  <Badge status={s.status} />
                );
              },
            }}
            onRowOpen={(s) => {
              const navOptions = recordSeqNavOptions(
                filtered.map((r) => ({
                  id: r.id,
                  label: (r.date || '') + ' · ' + ((r.herd && FLOCK_LABELS[r.herd]) || r.herd || 'sheep'),
                })),
              );
              // The destination batch rides alongside recordSeq in the SAME
              // state object — WeighInSessionPage reads both off one location
              // state, so merging here is what keeps the sequence controls and
              // the preselected batch from displacing each other.
              navigate('/weigh-in-sessions/' + s.id, {
                state: {...navOptions.state, ...(sendToBatchState || {})},
              });
            }}
            columns={[
              {
                key: 'label',
                label: 'Flock',
                primary: true,
                // R06: the flock's 7px group dot, the same palette slot the
                // flocks list gives it (index in SHEEP_FLOCK_KEYS).
                render: (s) => (
                  <RuminantGroupLabel
                    label={(s.herd && FLOCK_LABELS[s.herd]) || s.herd || 'Unknown flock'}
                    index={SHEEP_FLOCK_KEYS.indexOf(s.herd || '')}
                  />
                ),
              },
              {
                key: 'status',
                label: 'Status',
                render: (s) => <Badge status={s.status} />,
              },
              {key: 'date', label: 'Date', render: (s) => fmt(s.date)},
              {
                key: 'team',
                label: 'Team',
                mobilePriority: false,
                render: (s) => s.team_member || <StatusText tone="muted">{'—'}</StatusText>,
              },
              {
                key: 'entries',
                label: 'Entries',
                align: 'right',
                render: (s) => {
                  const sEntriesAll = entries[s.id] || [];
                  return tagQ
                    ? sEntriesAll.filter(entryMatchesTag).length + ' of ' + sEntriesAll.length + ' match'
                    : sEntriesAll.length + ' ' + (sEntriesAll.length === 1 ? 'entry' : 'entries');
                },
              },
              {
                key: 'newtags',
                label: 'New tags',
                align: 'right',
                mobilePriority: false,
                render: (s) => {
                  if (tagQ) return <StatusText tone="muted">{'—'}</StatusText>;
                  const newTagCount = (entries[s.id] || []).filter((e) => e.new_tag_flag).length;
                  if (newTagCount === 0) return <StatusText tone="muted">{'—'}</StatusText>;
                  return <StatusText tone="danger">{newTagCount + ' new'}</StatusText>;
                },
              },
            ]}
          />
        )}
      </div>
      {showNewModal && (
        <SheepNewWeighInModal
          authState={lockedAuthState}
          onClose={() => setShowNewModal(false)}
          onCreate={createNewSession}
        />
      )}
    </div>
  );
};

export default SheepWeighInsView;
